import { PlatformLocation } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { CLIENT_ENVIRONMENT } from '../tokens/environment';

/**
 * Service that handles the state of the routes.
 *
 * Right now it is not used and it might never be, but it can be useful to check
 * if the last navigation is finished.
 */
@Injectable({ providedIn: 'root' })
export class RoutesService {
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly environment = inject(CLIENT_ENVIRONMENT);
	private readonly location = inject(PlatformLocation);

	private navigationLoadingMap: Record<string, number> = {};
	private latestId: string;

	private navigating$$ = new BehaviorSubject(false);
	public navigating$ = this.navigating$$.asObservable();

	/**
	 * Map of ended navigations (cleaned - without query params) and the time they took to load.
	 */
	private navigationTimeMap: Record<string, number> = {};

	private loadingRoute$$ = new BehaviorSubject(false);
	/**
	 * Whether the last navigation has ended or not.
	 */
	public loadingRoute$ = this.loadingRoute$$.asObservable().pipe(takeUntilDestroyed());

	private waitingRoutes$$ = new BehaviorSubject(0);
	/**
	 * Number of routes that are pending to load.
	 */
	public waitingRoutes$ = this.waitingRoutes$$.asObservable().pipe(takeUntilDestroyed());

	private navigationFinishedErrors = 0;

	private lastNavigationId = 0;

	protected onStart(): void {
		// start of each navigation
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationStart),
				tap(() => this.navigating$$.next(true)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((event: NavigationStart) => this.handleNavigationStart(event));

		// end of each navigation
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				tap(() => this.navigating$$.next(false)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(async (event: NavigationEnd) => await this.handleNavigationEnd(event));

		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationCancel),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.navigating$$.next(false);
			});

		this.location.onPopState((event) => {
			const currentNavigationId = event.state?.id || 0;
			const back = currentNavigationId < this.lastNavigationId;

			this.lastNavigationId = currentNavigationId;

			const intermediateState = history.state?.intermediateState === true;
			const isIntermediate = !!intermediateState || typeof intermediateState === typeof 'str';

			if (isIntermediate) {
				console.debug(
					`Intermediate state found while navigating. Expanding navigation to handle it.`
				);

				if (back) {
					history.back();
				} else {
					history.forward();
				}
			}
		});
	}

	public async navigationFinished(): Promise<void> {
		return await firstValueFrom(
			this.navigating$.pipe(
				filter((navigating) => !navigating),
				map(() => null),
				take(1),
				takeUntilDestroyed(this.destroyRef)
			) as Observable<void>
		);
	}

	public async addIntermediateState(debugMsg?: string): Promise<number> {
		this.lastNavigationId += 1;
		console.debug(`Adding intermediate state "${debugMsg}" [${this.lastNavigationId}]`);
		const state = { id: this.lastNavigationId, intermediateState: true };
		history.pushState(state, null as unknown as string);

		return this.lastNavigationId;
	}

	/**
	 * @param url Url to clean
	 * @returns The url without the query params.
	 */
	private cleanUrl(url: string): string {
		return url.split('?')[0].trim() || '/';
	}

	/**
	 * Updates all the Services streams.
	 */
	private update(): void {
		this.loadingRoute$$.next(!!this.navigationLoadingMap[this.latestId]);
		this.waitingRoutes$$.next(Object.keys(this.navigationLoadingMap).length);
	}

	private handleNavigationStart(event: NavigationStart): void {
		this.navigationLoadingMap[`${event.id}`] = performance.now();
		this.latestId = `${event.id}`;
		this.update();
	}

	private async handleNavigationEnd(event: NavigationEnd): Promise<void> {
		this.lastNavigationId += 1;
		history.replaceState({ id: this.lastNavigationId }, null as unknown as string);

		const before = this.navigationLoadingMap[`${event.id}`];
		const time = performance.now() - before;
		const url = this.cleanUrl(event.url);
		this.navigationTimeMap[url] = Math.max(this.navigationTimeMap[url] || 0, time);
		delete this.navigationLoadingMap[`${event.id}`];
		this.update();

		if (!before) {
			this.navigationFinishedErrors++;
		}
	}
}
